import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { useDismissOnOutside } from "@/hooks/use-dismiss-on-outside";
import type { DismissReason } from "@/hooks/use-dismiss-on-outside";
import { useDropdownPlacement } from "@/hooks/use-dropdown-placement";

export type DropdownProps<T> = {
  data: T[];
  value?: T | T[];
  multiple?: boolean;
  renderItem: (item: T) => React.ReactNode;
  getItemValue: (item: T) => string;
  onChange: (selected: T[] | T | null) => void;
  placeholder?: string;
  isItemDisabled?: (item: T) => boolean;
  lookupKey?: keyof T;
};

/** The menu's natural cap — also the height the flip decision is measured against. */
const MENU_PREFERRED_HEIGHT = 240;

const Dropdown = <T,>({
  data,
  value,
  multiple = false,
  renderItem,
  getItemValue,
  onChange,
  placeholder = "Select...",
  isItemDisabled,
  lookupKey,
}: DropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  // The option the keyboard is currently on. -1 means "none"; DOM focus stays on the trigger
  // and this is surfaced to assistive tech via aria-activedescendant.
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedItems, setSelectedItems] = useState<T[]>(
    Array.isArray(value) ? value : value ? [value] : [],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const { placement, maxHeight } = useDropdownPlacement(triggerRef, open, {
    preferredHeight: MENU_PREFERRED_HEIGHT,
  });

  useEffect(() => {
    setSelectedItems(
      Array.isArray(value)
        ? value
        : value && typeof value === "object" && !Array.isArray(value)
          ? Object.values(value).every((v) => v === "")
            ? []
            : [value]
          : value
            ? [value]
            : [],
    );
  }, [value]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleDismiss = useCallback(
    (reason: DismissReason) => {
      close();
      // Only pull focus back for Escape — doing it on an outside click would steal focus from
      // whatever the user just pointed at.
      if (reason === "escape") triggerRef.current?.focus();
    },
    [close],
  );

  useDismissOnOutside(containerRef, open, handleDismiss);

  const toggleOpen = () => {
    if (!data) return;
    if (open) close();
    else setOpen(true);
  };

  const handleSelect = (item: T) => {
    if (isItemDisabled?.(item)) return;
    if (multiple) {
      const exists = selectedItems.some(
        (i) => getItemValue(i) === getItemValue(item),
      );
      const updated = exists
        ? selectedItems.filter((i) => getItemValue(i) !== getItemValue(item))
        : [...selectedItems, item];
      setSelectedItems(updated);
      onChange(updated);
    } else {
      setSelectedItems([item]);
      onChange(item);
      close();
    }
  };

  const isSelected = (item: T) => {
    if (lookupKey) {
      return selectedItems.some((i) => i[lookupKey] === item[lookupKey]);
    } else {
      return selectedItems.some((i) => getItemValue(i) === getItemValue(item));
    }
  };

  const getLabel = () => {
    if (selectedItems.length === 0)
      return (
        <span className="text-sm text-muted-foreground">{placeholder}</span>
      );
    if (!multiple) return getItemValue(selectedItems[0]);
    return selectedItems.map(getItemValue).join(", ");
  };

  /** First enabled option at or after `start`, walking in `step` direction. -1 when none. */
  const findEnabledFrom = (start: number, step: number) => {
    for (let i = start; i >= 0 && i < (data?.length ?? 0); i += step) {
      if (!isItemDisabled?.(data[i])) return i;
    }
    return -1;
  };

  const moveActive = (step: number) => {
    if (!data?.length) return;
    const start =
      activeIndex < 0 ? (step > 0 ? 0 : data.length - 1) : activeIndex + step;
    const next = findEnabledFrom(start, step);
    if (next !== -1) setActiveIndex(next);
  };

  const openWith = (index: number) => {
    setOpen(true);
    setActiveIndex(index);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (!data) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (open) moveActive(1);
        else openWith(findEnabledFrom(0, 1));
        return;
      case "ArrowUp":
        event.preventDefault();
        if (open) moveActive(-1);
        else openWith(findEnabledFrom(data.length - 1, -1));
        return;
      case "Home":
        if (!open) return;
        event.preventDefault();
        setActiveIndex(findEnabledFrom(0, 1));
        return;
      case "End":
        if (!open) return;
        event.preventDefault();
        setActiveIndex(findEnabledFrom(data.length - 1, -1));
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        if (!open) openWith(findEnabledFrom(0, 1));
        else if (activeIndex >= 0) handleSelect(data[activeIndex]);
        return;
      case "Tab":
        // Let focus move on, but do not leave an orphaned menu behind.
        if (open) close();
        return;
      default:
    }
  };

  // Keep the keyboard-active option in view as the arrows walk past the scroll edge.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const option = listRef.current?.children[activeIndex];
    // jsdom does not implement scrollIntoView, hence the optional call.
    (option as HTMLElement | undefined)?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  return (
    <div className="relative my-2 w-full" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
        onClick={toggleOpen}
        onKeyDown={handleTriggerKeyDown}
        className="flex h-9 w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="text-sm">{getLabel()}</div>
        <ChevronDown
          className={`ml-2 size-4 transition-transform duration-200 ${
            open && data ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && data && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-multiselectable={multiple}
          className={cn(
            "absolute z-[100] w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg",
            // mt-1 alone relied on the menu's static position; the explicit top-full/bottom-full
            // pair is what lets it anchor to either edge of the trigger.
            placement === "top" ? "bottom-full mb-1" : "top-full mt-1",
          )}
          style={{ maxHeight }}
        >
          {data?.map((item, index) => {
            const selected = isSelected(item);
            const disabled = isItemDisabled?.(item) ?? false;
            return (
              <li
                key={index}
                id={optionId(index)}
                role="option"
                aria-selected={selected}
                aria-disabled={disabled || undefined}
                onClick={() => !disabled && handleSelect(item)}
                onMouseEnter={() => !disabled && setActiveIndex(index)}
                className={`flex cursor-pointer items-center px-3 py-2 text-sm ${
                  selected ? "bg-gray-100" : ""
                } ${index === activeIndex && !disabled ? "bg-gray-50" : ""} ${
                  disabled
                    ? "cursor-not-allowed text-gray-400"
                    : "hover:bg-gray-50"
                }`}
              >
                {multiple && (
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    disabled={disabled}
                    // Decorative: `option` is children-presentational, and aria-selected on the
                    // row is what actually conveys state. Keep it out of the tab order and the
                    // accessibility tree — some browsers still expose a nested native checkbox,
                    // which would be announced as a second, unnamed control.
                    tabIndex={-1}
                    aria-hidden="true"
                    className="mr-2"
                  />
                )}
                {renderItem(item)}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
